import {
  FavouriteType,
  type FavouriteResponse,
  favouriteStoreTypes,
  groupFavouritesByType,
  useFavouritesStore,
} from '@/stores/favourites'
import { useApiClient } from '~/composables/api/api'
import { watch } from 'vue'

export const useFavourites = () => {
  const authStore = useAuthStore()
  const productStore = useFavouritesStore(FavouriteType.product)
  const riskAssessmentStore = useFavouritesStore(FavouriteType.riskAssessment)
  const documentStore = useFavouritesStore(FavouriteType.document)
  const hazardStore = useFavouritesStore(FavouriteType.hazard)
  const processingAidStore = useFavouritesStore(FavouriteType.processingAid)
  const locationStore = useFavouritesStore(FavouriteType.location)
  const stores = [
    productStore,
    riskAssessmentStore,
    documentStore,
    hazardStore,
    processingAidStore,
    locationStore,
  ]
  const storesByType = new Map(favouriteStoreTypes.map((type, index) => [type, stores[index]!]))
  const { isPublicRoute } = useAuthentication()

  const initializeFavourites = async () => {
    if (!authStore.canUseRoleProtectedPortalFeatures()) {
      for (const store of stores) store.updateFavourites([])
      return
    }

    try {
      for (const store of stores) {
        store.hasLoaded = false
        store.fetchingFavourites = true
      }

      const response = await useApiClient<{ values: FavouriteResponse[] }>('/portal/favourites', {
        method: 'GET',
      })

      const grouped = groupFavouritesByType(response.values)
      for (const [kindOf, store] of storesByType) {
        store.updateFavourites(grouped[kindOf])
      }

      for (const store of stores) store.fetchingFavourites = false
    } catch (error) {
      for (const store of stores) store.fetchingFavourites = false
      console.error('Failed to initialize favourites:', error)
    }
  }

  watch(
    () => [isPublicRoute.value, authStore.roles],
    ([publicRoute]) => {
      if (!publicRoute) initializeFavourites()
    },
    { immediate: true },
  )

  return {
    productStore,
    riskAssessmentStore,
    documentStore,
    hazardStore,
    processingAidStore,
    locationStore,
  }
}
